import React, { useEffect } from 'react';
import { AppProps } from 'next/app';
import Head from 'next/head';
import Script from 'next/script';
import { useRouter } from 'next/router';
import { ThemeProvider } from 'styled-components';
import GlobalStyle from 'styles/global-styles';
import { darkTheme, lightTheme } from 'styles/themes';
import { AnalyticProvider } from 'contexts/analytic-context';
import { UserProvider } from 'contexts/user-context';
import registerErrorHandlers from 'helpers/error-handlers';
import { ApolloProvider } from '@apollo/client';
import { datadogLogs } from '@datadog/browser-logs';
import Bugsnag from 'helpers/bugsnag';
import { useApollo } from 'api/apollo/client';
import { ON_CLIENT } from 'helpers/context';
import * as fbq from 'helpers/fb-pixel';
import Intercom from 'containers/intercom';
import Segment from 'containers/segment';
import type { NextComponentType, NextPageContext } from 'next';
import type { GetDefaultPropsReturn } from 'helpers/ssr-props/default-props';
import { logger } from 'helpers/logger';
import useMountEffect from 'hooks/use-mount-effect';
import { Merge } from 'helpers/typescript';
import { decodeBase64EncodedStringAndReturnId } from 'helpers/common';
import ErrorPage from './_error';

const noOpWrapper = ({ children }): JSX.Element => <>{children}</>;

const ErrorBoundary =
  process.env.NODE_ENV === 'production'
    ? Bugsnag.getPlugin('react').createErrorBoundary(React)
    : noOpWrapper;

type InnerAppProps = {
  Component: NextComponentType<NextPageContext, any, GetDefaultPropsReturn>;
  pageProps: Merge<GetDefaultPropsReturn, { disablePageBackground?: boolean }>;
};

const AuthedApp: React.FC<InnerAppProps> = ({ Component, pageProps }) => (
  <>
    <UserProvider authenticated initialUser={pageProps.user}>
      <Intercom authenticated />
      <AnalyticProvider>
        <Component {...pageProps} />
        <Segment />
      </AnalyticProvider>
    </UserProvider>
  </>
);

const UnauthedApp: React.FC<InnerAppProps> = ({ Component, pageProps }) => (
  <>
    <Intercom authenticated={false} />
    <AnalyticProvider>
      <Component {...pageProps} />
      <Segment />
    </AnalyticProvider>
  </>
);

const App: React.FC<AppProps<GetDefaultPropsReturn>> = ({
  Component,
  pageProps,
}) => {
  const apolloClient = useApollo(pageProps, pageProps?.userToken);
  const router = useRouter();

  registerErrorHandlers();

  useEffect(() => {
    fbq.pageview();
    const handleRouteChange = (): void => {
      fbq.pageview();
    };
    router.events.on('routeChangeComplete', handleRouteChange);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router.events]);

  useMountEffect(() => {
    if (process.env.MAINTENANCE_MODE) {
      if (!(router.pathname === '/maintenance')) {
        router.push('/maintenance');
      }
    }
    logger('log', pageProps, {
      location: `${router.pathname}`,
      context: '_app default pageProps',
    });
  });

  useEffect(() => {
    logger('log', `route is now ${router.pathname}`, {
      location: router.pathname,
      context: '_app',
    });
  }, [router.pathname]);

  return (
    <ErrorBoundary FallbackComponent={ErrorPage}>
      <ThemeProvider
        theme={pageProps?.theme === 'light' ? lightTheme : darkTheme}
      >
        <GlobalStyle />
        <Script
          id="fb-pixel"
          strategy="afterInteractive"
          dangerouslySetInnerHTML={{
            __html: `
            !function(f,b,e,v,n,t,s)
            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window, document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');
            fbq('init', ${process.env.FB_PIXEL_ID});
          `,
          }}
        />
        <ApolloProvider client={apolloClient}>
          <Head>
            <title>Players&apos; Lounge Connect</title>
            <meta
              name="viewport"
              content="width=device-width, initial-scale=1, maximum-scale=1.0, user-scalable=no, viewport-fit=cover"
            />
          </Head>
          {pageProps.authenticated ? (
            <AuthedApp Component={Component} pageProps={pageProps} />
          ) : (
            <UnauthedApp Component={Component} pageProps={pageProps} />
          )}
        </ApolloProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
};

export default App;

if (ON_CLIENT) {
  datadogLogs.init({
    clientToken: process.env.DD_RUM_CLIENT_TOKEN || '',
    site: process.env.DD_RUM_SITE,
    service: process.env.DD_RUM_SERVICE,
    version: process.env.VERSION_SHA,
    env: process.env.PL_ENV,
    forwardErrorsToLogs: true,
    sessionSampleRate: 100,
    beforeSend: (log) => {
      // remove email from view url
      if (log?.user_id) {
        const decodedId = decodeBase64EncodedStringAndReturnId(
          `${log.user_id}`,
        );
        // eslint-disable-next-line no-param-reassign
        log.user_id = decodedId;
      }
    },
  });
}
